import { useEffect, useRef, useState } from 'react';
import * as S from '../../domain/sceneOps';
import type { Asset } from '../../domain/schemas';
import { createId, nowIso } from '../../lib/ids';
import { useObservable } from '../../lib/useObservable';
import { IMAGE_ACCEPT, IMAGE_HEADER_BYTES, ImageFileError, MAX_IMAGE_BYTES, MAX_IMAGE_SIDE, validateImageFile } from '../../scene/imageFormats';
import { useProject } from '../../state/projectContext';
import { useServices } from '../../state/services';
import { StorageError, toStorageError } from '../../storage/errors';
import { withAssetRemoved } from '../../storage/projectRepository';
import { Alert, Button, ConfirmDialog, Panel, Spinner } from '../ui';
import type { EditFn } from './useSceneEdit';

/**
 * Validates and stores an image: signature and header dimensions first (no decode), then
 * a real decode to prove the browser can read it (released immediately), then one atomic
 * repository write of the metadata and the file.
 */
async function importImage(file: File, decode: (blob: Blob) => Promise<{ width: number; height: number; close?: () => void }>) {
  const header = new Uint8Array(await file.slice(0, IMAGE_HEADER_BYTES).arrayBuffer());
  const info = validateImageFile(file.name, file.size, header);
  const data = await file.arrayBuffer();
  let bitmap: { width: number; height: number; close?: () => void };
  try {
    bitmap = await decode(new Blob([data], { type: info.mimeType }));
  } catch {
    throw new ImageFileError('corrupt', `“${file.name}” could not be decoded. It may be damaged, or this browser cannot read ${info.format.toUpperCase()}.`);
  }
  const { width, height } = bitmap;
  bitmap.close?.();
  if (width !== info.width || height !== info.height) throw new ImageFileError('corrupt', `“${file.name}” has an inconsistent header (${info.width}×${info.height} vs ${width}×${height}).`);
  const asset: Asset = {
    id: createId('ast'),
    name: file.name.slice(0, 200) || 'image',
    kind: 'image',
    mimeType: info.mimeType,
    sizeBytes: data.byteLength,
    source: 'local-file',
    createdAt: nowIso(),
    image: { width, height },
  };
  return { asset, data };
}

function Thumb({ asset }: { asset: Asset }) {
  const { repo } = useServices();
  const [url, setUrl] = useState<string | null>(null);
  const [missing, setMissing] = useState(false);
  useEffect(() => {
    let objectUrl: string | null = null;
    let cancelled = false;
    repo.getBlob(asset.id).then(
      (blob) => {
        if (cancelled) return;
        if (!blob) return setMissing(true);
        objectUrl = URL.createObjectURL(new Blob([blob.data], { type: blob.mimeType }));
        setUrl(objectUrl);
      },
      () => !cancelled && setMissing(true),
    );
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [repo, asset.id]);
  if (missing) return <span className="grid size-14 place-items-center rounded bg-red-950 text-[10px] text-red-200">missing</span>;
  return url ? <img src={url} alt="" loading="lazy" decoding="async" className="size-14 rounded bg-zinc-800 object-contain" /> : <span className="size-14 rounded bg-zinc-800" />;
}

export function ImageLibrary({ edit, onPlace }: { edit: EditFn; onPlace: (assetId: string) => void }) {
  const { project, session, update } = useProject();
  const { repo, selection } = useServices();
  const selected = useObservable(selection);
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<{ title: string; details: string[] } | null>(null);
  const [deleting, setDeleting] = useState<Asset | null>(null);
  const images = project.assets.filter((a) => a.kind === 'image');
  const scene = S.activeSceneOf(project);

  const onFiles = async (files: File[]) => {
    setBusy(true);
    setError(null);
    try {
      for (const file of files) {
        try {
          const { asset, data } = await importImage(file, (blob) => createImageBitmap(blob));
          if (!(await session.saveNow())) throw new StorageError('transaction', 'Save the pending changes first.');
          const saved = await repo.addAsset(session.snapshot.project!, asset, data);
          update((p) => ({ ...p, assets: saved.assets }));
          selection.selectImage(asset.id);
        } catch (e) {
          const err = e instanceof ImageFileError ? { title: e.message, details: [] } : (() => {
            const s = toStorageError(e, `“${file.name}” could not be saved.`);
            return { title: s.message, details: s.details };
          })();
          setError(err);
          break;
        }
      }
    } finally {
      setBusy(false);
    }
  };

  const remove = async (asset: Asset) => {
    setBusy(true);
    try {
      if (!(await session.saveNow())) throw new StorageError('transaction', 'Save the pending changes first.');
      const saved = await repo.removeAsset(session.snapshot.project!, asset.id, { detach: true });
      update((p) => withAssetRemoved(p, saved));
      if (selection.get().imageId === asset.id) selection.selectImage(null);
    } catch (e) {
      const s = toStorageError(e, 'The image could not be deleted.');
      setError({ title: s.message, details: s.details });
    } finally {
      setBusy(false);
    }
  };

  const usageText = (a: Asset) => {
    const u = S.imageUsage(project, a.id);
    return [u.objects.length ? `${u.objects.length} object${u.objects.length > 1 ? 's' : ''}` : '', u.backgrounds.length ? `${u.backgrounds.length} background${u.backgrounds.length > 1 ? 's' : ''}` : ''].filter(Boolean).join(', ');
  };

  return (
    <Panel
      title="Images"
      headingLevel={3}
      actions={
        <>
          <Button size="sm" onClick={() => input.current?.click()} disabled={busy}>
            Import image…
          </Button>
          <input
            ref={input}
            type="file"
            multiple
            accept={IMAGE_ACCEPT}
            className="sr-only"
            tabIndex={-1}
            aria-label="Import images"
            data-testid="import-image"
            onChange={(e) => {
              const files = [...(e.target.files ?? [])];
              e.target.value = '';
              void onFiles(files);
            }}
          />
        </>
      }
    >
      <p className="mb-2 text-xs text-zinc-400">
        PNG, JPEG or WebP, checked by content · up to {MAX_IMAGE_BYTES / 1048576} MB and {MAX_IMAGE_SIDE} px per side. Stored only in this browser and shared with the project’s audio budget.
      </p>
      <div aria-live="polite">
        {busy && <Spinner label="Checking and saving…" />}
        {error && (
          <Alert title={error.title} action={<Button size="sm" variant="ghost" onClick={() => setError(null)}>Dismiss</Button>}>
            {error.details.length > 0 && <ul className="list-disc pl-4">{error.details.map((d) => <li key={d}>{d}</li>)}</ul>}
          </Alert>
        )}
      </div>
      {images.length === 0 ? (
        <p className="text-sm text-zinc-400">No images yet.</p>
      ) : (
        <ul className="flex flex-col gap-1.5" aria-label="Images" data-testid="image-list">
          {images.map((a) => {
            const isSel = selected.imageId === a.id;
            const usage = usageText(a);
            return (
              <li key={a.id} className={`flex items-center gap-2 rounded-md border p-1.5 ${isSel ? 'border-accent-400' : 'border-zinc-800'}`}>
                <Thumb asset={a} />
                <button type="button" aria-pressed={isSel} className="min-w-0 flex-1 text-left" onClick={() => selection.selectImage(isSel ? null : a.id)}>
                  <span className="block truncate text-sm">{a.name}</span>
                  <span className="text-xs text-zinc-400">
                    {a.image ? `${a.image.width}×${a.image.height}` : ''} · {(a.sizeBytes / 1024).toFixed(0)} KB{usage ? ` · used: ${usage}` : ''}
                  </span>
                </button>
                <div className="flex flex-col gap-1">
                  <Button size="sm" onClick={() => onPlace(a.id)}>
                    Add<span className="sr-only"> {a.name} to the scene</span>
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => edit('Background image', (p) => S.setBackgroundImage(p, scene.id, { assetId: a.id, fit: 'cover', scale: 1, offsetX: 0, offsetY: 0, opacity: 1 }))}>
                    Background<span className="sr-only"> from {a.name}</span>
                  </Button>
                  <Button size="sm" variant="ghost" className="text-red-300" onClick={() => setDeleting(a)} disabled={busy}>
                    Delete<span className="sr-only"> {a.name}</span>
                  </Button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
      <ConfirmDialog
        open={deleting !== null}
        danger
        title={`Delete “${deleting?.name ?? ''}”?`}
        body={deleting && usageText(deleting) ? `This image is used (${usageText(deleting)}). Deleting it removes those uses too. This cannot be undone.` : 'The image is removed from this project and from browser storage. This cannot be undone.'}
        confirmLabel={deleting && usageText(deleting) ? 'Delete image and its uses' : 'Delete image'}
        onCancel={() => setDeleting(null)}
        onConfirm={() => {
          const a = deleting!;
          setDeleting(null);
          void remove(a);
        }}
      />
    </Panel>
  );
}
